import { useState, useMemo, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import type { BusinessInvitation, BusinessSubscription, LensSDKException } from "lens-platform-sdk";
import { Button, Modal } from "@k8slens/lds";

import { useAnalytics } from "src/hooks/useAnalytics";
import { getPlanName } from "src/services/plans";
import { getSubscriptionSeatCount } from "src/services/getSubscriptionSeatCount";

import Notification from "src/components/Notification/Notification";
import Select from "src/components/Select/Select";
import SubscriptionSelectContent from "src/app/business/[businessId]/users/SubscriptionSelectContent";

import styles from "src/components/Business/AssignSubscriptionModal.module.css";
import { getUniqueEmailsArray, validateEmail } from "src/services/email";
import { isAssignableSubscription } from "src/services/getSubscriptionState";

interface Props {
  open: boolean;
  assigningSubscription: boolean;
  assignSubscriptionError?: LensSDKException;
  disabled: boolean;
  onModalClose: () => void;
  loadingBusinessSubscriptions: boolean;
  loadingBusinessInvitations: boolean;
  businessSubscriptions: BusinessSubscription[];
  businessInvitations: BusinessInvitation[];
  onClickAssign: (
    assigneeEmails: string,
    selectedBusinessSubscription: BusinessSubscription,
  ) => Promise<LensSDKException | null>;
}

const cancelButtonLabel = "Cancel";
const title = "Assign Seats";
const selectSubscriptionLabel = "Choose Lens Subscription";

/**
 * The shape of the option data in the business subscription selector
 */
export interface SubscriptionOption {
  id: BusinessSubscription["id"];
  label: string;
}

/**
 * Get the shape of the option data to be used in the business subscription selector
 */
const getSubscriptionOption = (businessSubscription: BusinessSubscription): SubscriptionOption | undefined => {
  if (!businessSubscription) {
    return undefined;
  }

  return {
    id: businessSubscription.id,
    label: getPlanName(businessSubscription.planCode),
  };
};

const emailRequiredMessage = "Required";
const emailInvalidMessage = "Invalid email address(es)";

const assignButtonLabel = "Assign";
const addNewSubscriptionLabel = "Add new Subscription";

const AssignSubscriptionModal = ({
  open,
  assigningSubscription,
  assignSubscriptionError,
  disabled,
  onModalClose,
  loadingBusinessSubscriptions,
  loadingBusinessInvitations,
  businessSubscriptions,
  businessInvitations,
  onClickAssign,
}: Props) => {
  const { track, trackButtonClicked } = useAnalytics();
  const history = useHistory();

  const handleNewSubscriptions = () => {
    trackButtonClicked(addNewSubscriptionLabel);
    history.push("/subscribe/pro-yearly/configure");
  };
  const [selectedBusinessSubscription, setSelectedBusinessSubscription] = useState<BusinessSubscription>();
  const seatCount = useMemo(
    () => getSubscriptionSeatCount(businessInvitations, selectedBusinessSubscription),
    [businessInvitations, selectedBusinessSubscription],
  );
  const [assigneeEmails, setAssigneeEmails] = useState<string>("");
  const [assigneeEmailsValid, setAssigneeEmailsValid] = useState<boolean>(false);
  const [assigneeEmailsErrorMessage, setAssigneeEmailsErrorMessage] = useState<string>("");

  const assignableSubscriptions = useMemo(
    () => businessSubscriptions.filter(isAssignableSubscription),
    [businessSubscriptions],
  );
  const defaultOption = useMemo(() => getSubscriptionOption(assignableSubscriptions[0]), [assignableSubscriptions]);
  const [selected, setSelected] = useState<SubscriptionOption>();
  const options = useMemo<SubscriptionOption[]>(
    () =>
      assignableSubscriptions
        .map(getSubscriptionOption)
        // filter (unlikely) undefined returned from getOption
        .filter((option): option is SubscriptionOption => typeof option?.id === "string")
        // sort by label alphabetically
        .sort((a, b) => a.label.localeCompare(b.label)),
    [assignableSubscriptions],
  );

  const chooseSubscription = useCallback(
    (option: SubscriptionOption | undefined) => {
      if (!option) {
        return;
      }
      track("Subscription Selected", { subscriptionId: option.id });
      setSelected(option);
    },
    [track, setSelected],
  );

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setAssigneeEmails("");
    setAssigneeEmailsValid(false);
    setAssigneeEmailsErrorMessage("");
    chooseSubscription(defaultOption);
  }, [defaultOption, chooseSubscription]);

  /**
   * Return the errorMessage if the assignee emails are invalid or undefined if valid
   */
  const assigneeEmailsValidate = useCallback((emails: string, available: number) => {
    // assigneeEmails is a empty string
    if (emails.length === 0) {
      return emailRequiredMessage;
    }

    const emailsArray = getUniqueEmailsArray(emails);
    const isAllEmailValid = emailsArray.every(validateEmail);

    if (!isAllEmailValid) {
      return emailInvalidMessage;
    }

    if (emailsArray.length > available === true) {
      return `Not enough seats available for ${emailsArray.length} users in this subscription`;
    }

    return undefined;
  }, []);

  // set the default selected business subscription
  useEffect(() => {
    chooseSubscription(defaultOption);
  }, [defaultOption, chooseSubscription]);

  // "sync" the selected business subscription with the selected option
  useEffect(() => {
    setSelectedBusinessSubscription(businessSubscriptions.find(({ id }) => id === selected?.id));
  }, [selected, businessSubscriptions]);

  useEffect(() => {
    const result = assigneeEmailsValidate(assigneeEmails, seatCount.available);

    setAssigneeEmailsValid(result === undefined);
    setAssigneeEmailsErrorMessage(result ?? "");
  }, [assigneeEmails, assigneeEmailsValidate, seatCount.available]);

  const handleSubmit = async () => {
    if (selectedBusinessSubscription) {
      trackButtonClicked(assignButtonLabel);
      const res = await onClickAssign(assigneeEmails, selectedBusinessSubscription);

      if (!res) {
        reset();
      }
    }
  };

  return (
    <Modal
      title={title}
      size="md"
      contentProps={{
        className: styles.assignBusinessSubscriptionModalContent,
      }}
      className={styles.assignBusinessSubscriptionModalPanel}
      onClose={() => {
        if (!assigningSubscription) {
          onModalClose();
          reset();
        }
      }}
      isOpen={open}
      footer={
        <div className={styles.footerButtonbar}>
          <button
            aria-label={cancelButtonLabel}
            type="button"
            onClick={() => {
              if (!assigningSubscription) {
                trackButtonClicked(cancelButtonLabel);
                onModalClose();
                reset();
              }
            }}
            className="lds-button"
          >
            {cancelButtonLabel}
          </button>
          <Button
            loading={assigningSubscription || loadingBusinessSubscriptions || loadingBusinessInvitations}
            label={assignButtonLabel}
            id="assignSubscriptionButton"
            buttonType="submit"
            primary
            onClick={handleSubmit}
            disabled={disabled || assigneeEmailsValid === false || !selectedBusinessSubscription}
          />
        </div>
      }
    >
      <form aria-label={title} autoComplete="off">
        {assignSubscriptionError ? (
          <Notification
            level="error"
            message={assignSubscriptionError?.message ?? "unknown"}
            type="flash"
            className={styles.errorNotification}
          />
        ) : null}
        <fieldset>
          <div className={styles.chooseLensSubscriptionLabel} aria-hidden>
            <span>{selectSubscriptionLabel}</span>
          </div>
          <Select
            aria-label={selectSubscriptionLabel}
            className={styles.selectBusinessSubscription}
            optionClassName={(state) => (state?.active === true ? styles.selectBusinessSubscriptionActive : "")}
            loading={loadingBusinessSubscriptions}
            value={selected}
            onChange={(value) => {
              chooseSubscription(value);
            }}
            options={options}
            renderContent={(value) =>
              value ? (
                <SubscriptionSelectContent
                  id={value.id}
                  businessSubscriptions={businessSubscriptions}
                  businessInvitations={businessInvitations}
                />
              ) : (
                ""
              )
            }
            renderAfterLastOption={() => (
              <button
                aria-label={addNewSubscriptionLabel}
                type="button"
                onClick={handleNewSubscriptions}
                className={styles.newSubscriptionButton}
              >
                {addNewSubscriptionLabel}
              </button>
            )}
          />
          <div className={styles.assigneeEmailTextareaLabel}>
            <span>Add Users to Assign</span>
          </div>
          <textarea
            value={assigneeEmails}
            onChange={({ target: { value } }) => {
              setAssigneeEmails(value);
            }}
            autoComplete="off"
            name="assigneeEmails"
            placeholder="Comma separated list of user Email addresses"
            className={styles.assigneeEmailTextarea}
            spellCheck="false"
          />
          {assigneeEmailsErrorMessage.length > 0 ? (
            <div className={styles.assigneeEmailsErrorMessage}>
              <span>{assigneeEmailsErrorMessage}</span>
            </div>
          ) : null}
          {assigneeEmailsValid && selectedBusinessSubscription ? (
            <div className={styles.assigneeEmailsMessage}>
              <span>{`Assign ${getPlanName(selectedBusinessSubscription.planCode)} subscription seats to ${
                getUniqueEmailsArray(assigneeEmails).length
              } user(s)`}</span>
            </div>
          ) : null}
        </fieldset>
      </form>
    </Modal>
  );
};

export default AssignSubscriptionModal;
